/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Icon,
  Option,
  Select,
  Stack,
  Switch,
  Text,
  Input,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import isEqual from 'lodash/isEqual';
import { TitleLabel } from 'src/components/Common/FormComponent';
import { DlpPolicyItem } from 'src/pages/Policy/utils/types';
import ComplianceRules from './ComplianceRules';
import ApplyBound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ApplyBound';
import { checkDlpScanTargetHeader } from '../../../../utils/fns';

export const DlpRuleStatus = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get state from store
  const dpEnabled = policySelector((s) => s.tmDpfilters.dpEnabled);
  // get action from store
  const setDpState = contextSelector((s) => s.setDpState);
  useWhyDidYouUpdate('DlpRuleStatus', { dpEnabled });
  return (
    <Flex alignItems="center" pt="2x" pb="4x">
      <Switch
        mr="1x"
        checked={!!dpEnabled}
        value={dpEnabled}
        onChange={() => {
          setDpState({ dpEnabled: dpEnabled === 1 ? 0 : 1 });
        }}
      />
      <Text mr="8px" fontSize="18px" lineHeight="24px">
        {t('policy.label.dlp')}
      </Text>
    </Flex>
  );
};

export const DlpApplyBound = () => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get state from store
  const dpApplyBound = policySelector((s) => s.tmDpfilters.dpApplyBound);
  const options = contextSelector(
    (s) => s.config.exchangeOnlineApplyBoundOptions,
    isEqual
  );
  // get action from store
  const setDpState = contextSelector((s) => s.setDpState);
  useWhyDidYouUpdate('DlpApplyBound', { dpApplyBound });
  return (
    <ApplyBound
      applyBoundValue={dpApplyBound}
      setApplyBound={setDpState}
      applyBoundKey="dpApplyBound"
      options={options}
    />
  );
};

export const DlpDisplayViolatingContent = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get state from store
  const dpViolationContentEnabled = policySelector(
    (s) => s.tmDpfilters.dpViolationContentEnabled
  );
  const dpViolationContentMode = policySelector(
    (s) => s.tmDpfilters.dpViolationContentMode
  );
  // get action from store
  const setDpState = contextSelector((s) => s.setDpState);
  useWhyDidYouUpdate('DlpDisplayViolatingContent', {
    dpViolationContentEnabled,
    dpViolationContentMode,
  });
  return (
    <Flex mt="28px">
      <Checkbox
        checked={!!dpViolationContentEnabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setDpState({
            dpViolationContentEnabled: e.target.checked ? 1 : 0,
          });
        }}
      >
        <Flex alignItems="center" flex="initial" justify="flex-start">
          <Text fontSize="14px" lineHeight="20px">
            {t('policy.dlp.displayViolatingContent')}
          </Text>
          <Select
            ml="8px"
            value={dpViolationContentMode}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setDpState({
                dpViolationContentMode: Number(e.target.value),
              });
            }}
            disabled={!dpViolationContentEnabled}
            width="140px"
          >
            <Option value={0}>{t('policy.dlp.masked')}</Option>
            <Option value={1}>{t('policy.dlp.unmasked')}</Option>
          </Select>
        </Flex>
      </Checkbox>
    </Flex>
  );
};

export const DlpScanTarget = ({
  showHeaderScan,
}: {
  showHeaderScan?: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get state from store
  const dpSubjectScanEnabled = policySelector(
    (s) => s.tmDpfilters.dpSubjectScanEnabled
  );
  const dpBodyScanEnabled = policySelector(
    (s) => s.tmDpfilters.dpBodyScanEnabled
  );
  const dpAttachmentScanEnabled = policySelector(
    (s) => s.tmDpfilters.dpAttachmentScanEnabled
  );
  const dpMailHeadersScanEnabled = policySelector(
    (s) => s.tmDpfilters.dpMailHeadersScanEnabled
  );
  const dpMailHeaders = policySelector((s) => s.tmDpfilters.dpMailHeaders);
  // get action from store
  const setDpState = contextSelector((s) => s.setDpState);
  useWhyDidYouUpdate('DlpScanTarget', {
    dpSubjectScanEnabled,
    dpBodyScanEnabled,
    dpAttachmentScanEnabled,
  });

  const mailHeadersError = useMemo(() => {
    return !!checkDlpScanTargetHeader({
      dpMailHeadersScanEnabled,
      dpMailHeaders,
    });
  }, [dpMailHeadersScanEnabled, dpMailHeaders]);

  return (
    <Stack mt="16px" gap="16px">
      <TitleLabel title={t('policy.dlp.scanTarget.title')} />
      <Flex gap="20px" alignItems="flex-start">
        <Checkbox
          checked={!!dpSubjectScanEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDpState({
              dpSubjectScanEnabled: e.target.checked ? 1 : 0,
            });
          }}
        >
          <Text lineHeight="20px" fontSize="14px">
            {t('policy.dlp.scanTarget.subject')}
          </Text>
        </Checkbox>
        <Checkbox
          checked={!!dpBodyScanEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDpState({
              dpBodyScanEnabled: e.target.checked ? 1 : 0,
            });
          }}
        >
          <Text lineHeight="20px" fontSize="14px">
            {t('policy.dlp.scanTarget.body')}
          </Text>
        </Checkbox>
        <Checkbox
          checked={!!dpAttachmentScanEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDpState({
              dpAttachmentScanEnabled: e.target.checked ? 1 : 0,
            });
          }}
        >
          <Text lineHeight="20px" fontSize="14px">
            {t('policy.dlp.scanTarget.attachment')}
          </Text>
        </Checkbox>
      </Flex>
      {showHeaderScan && (
        <Stack gap="8px" alignItems="flex-start">
          <Checkbox
            checked={!!dpMailHeadersScanEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDpState({
                dpMailHeadersScanEnabled: e.target.checked ? 1 : 0,
              });
            }}
          >
            <Text lineHeight="20px" fontSize="14px">
              {t('policy.dlp.scanTarget.header')}
            </Text>
          </Checkbox>

          <Input
            disabled={!dpMailHeadersScanEnabled}
            width="700px"
            value={dpMailHeaders}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDpState({
                dpMailHeaders: e.target.value,
              });
            }}
            maxLength={1000}
            error={mailHeadersError}
          />
          <Text color={colorStyle.color.secondary}>
            {t('policy.dlp.scanTarget.headerDesc')}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export const ComplianceRuleNote = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  return (
    <Flex gap="8px" align="stretch" pt="16px">
      <Icon as={InfoOIcon} color={colorStyle.color.info} />
      <Text fontSize="semibold">{t('common.label.note')}:</Text>
      <Text> {t('dlp.ComplianceRule.desc')}</Text>
    </Flex>
  );
};

const Rules = ({
  applyBoundShow,
  scanTargetShow,
  complianceRuleNoteShow = false,
  advancedOptionsShow = false,
  fileAdvancedOptionsShow = false,
  mipShow = false,
  advancedSettingsShowSalesforce = false,
  showHeaderScan = false,
}: {
  applyBoundShow: boolean;
  scanTargetShow: boolean;
  complianceRuleNoteShow?: boolean;
  advancedOptionsShow?: boolean;
  fileAdvancedOptionsShow?: boolean;
  advancedSettingsShowSalesforce?: boolean;
  mipShow?: boolean;
  showHeaderScan?: boolean;
}) => {
  console.log('render dlp rules');

  return (
    <Box>
      <DlpRuleStatus />
      {applyBoundShow ? <DlpApplyBound /> : null}
      <DlpDisplayViolatingContent />
      <Divider mt="10px" mb="10px" />
      {scanTargetShow ? (
        <DlpScanTarget showHeaderScan={showHeaderScan} />
      ) : null}
      <ComplianceRules
        complianceRuleNoteShow={complianceRuleNoteShow}
        advancedOptionsShow={advancedOptionsShow}
        fileAdvancedOptionsShow={fileAdvancedOptionsShow}
        mipShow={mipShow}
        advancedSettingsShowSalesforce={advancedSettingsShowSalesforce}
      />
    </Box>
  );
};

export default React.memo(Rules);
